// libraries
import { Frequency, RRule, Weekday } from "rrule";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

// utilities
import { pymod } from "../rrule";
import { createRRule } from "./createRRule";

// types
import { SimpleFreqValue } from "../../typings/scheduler/scheduler-core";

dayjs.extend(utc);
dayjs.extend(tz);

export function simpleFreqToRRule(
  startDate: Date | undefined,
  frequencyPresetString: SimpleFreqValue,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  if (!startDate) {
    return undefined;
  }
  switch (frequencyPresetString) {
    case "not-repeat":
      return createRRule(
        {
          dtstart: startDate,
          freq: Frequency.DAILY,
          count: 1,
          byhour: options.hours,
          byminute: options.mins,
        },
        options.timeZone,
      );
    case "daily":
      return dailyFreq(startDate, options);
    case "weekly":
      return weeklyDowFreq(startDate, options);
    case "monthly":
      return monthlyOnFirstDowFreq(startDate, options);
    case "yearly":
      return yearlyMonthDateFreq(startDate, options);
    case "weekday":
      return weekdayFreq(startDate, options);
    case "custom":
      return undefined;
  }
}

/**
 * internal functions
 */
function dailyFreq(
  dt: Date,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  return createRRule(
    {
      dtstart: dt,
      freq: Frequency.DAILY,
      byhour: options.hours,
      byminute: options.mins,
    },
    options.timeZone,
  );
}

function weeklyDowFreq(
  dt: Date,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  const dayInd = pymod(dayjs(dt).tz(options.timeZone).day() - 1, 6);

  const rule = createRRule(
    {
      dtstart: dt,
      freq: Frequency.WEEKLY,
      byweekday: dayInd,
      byhour: options.hours,
      byminute: options.mins,
    },
    options.timeZone,
  );
  return rule;
}

function monthlyOnFirstDowFreq(
  dt: Date,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  const wd = new Weekday(pymod(dt.getDay() - 1, 6));
  return createRRule(
    {
      dtstart: dt,
      freq: Frequency.MONTHLY,
      byweekday: wd,
      bysetpos: 1,
      byhour: options.hours,
      byminute: options.mins,
    },
    options.timeZone,
  );
}

function yearlyMonthDateFreq(
  dt: Date,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  const dayInd = pymod(dayjs(dt).tz(options.timeZone).day() - 1, 6);
  const wd = new Weekday(dayInd);
  return createRRule(
    {
      dtstart: dt,
      freq: Frequency.YEARLY,
      bymonth: dt.getMonth(),
      byweekday: wd,
      byhour: options.hours,
      byminute: options.mins,
    },
    options.timeZone,
  );
}

function weekdayFreq(
  dt: Date,
  options: {
    timeZone: string;
    hours?: number;
    mins?: number;
  },
) {
  return createRRule(
    {
      dtstart: dt,
      freq: Frequency.WEEKLY,
      byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
      byhour: options.hours,
      byminute: options.mins,
    },
    options.timeZone,
  );
}
